import { Component } from '~preact';
import PropTypes from "prop-types";
import withStoreContext from "./withStoreContext";
import { IS } from "@green24/npm-javascript-utils";

const missingProviderErrorMessage = "Connection to the store could not be established. Make sure that the store's context provider is set up, up in the hierarchy.";

/**
 * With store connection
 * ---
 */
export const withStoreConnection = (mapper = () => null, deepCheck = false, comparator = undefined, passStoreContext = false) => {
	return (ChildComponent, Context) => {
		class HOC extends Component {
			constructor(props) {
				super(props);

				this.state = {
					mappedStates: this._getMappedStates(props.storeContext),
				};

				this._mounted = false;
				this._previousMappedState = null;
				this._storeListenerUnsubscribe = null;
			}

			componentDidMount() {
				this._mounted = true;
				const {storeContext} = this.props;

				if(storeContext) {
					this._storeListenerUnsubscribe = storeContext.subscribe(() => this._updateStoreState());
				}
				else {
					throw Error(missingProviderErrorMessage);
				}
			}

			componentWillUnmount() {
				this._mounted = false;
				this._storeListenerUnsubscribe && this._storeListenerUnsubscribe();
			}

			render({storeContext, storePassContext, mapStatesToProps, storeDeepCheck, storeComparator, ...props}, {mappedStates}) {
				return (
					<ChildComponent
						{...(
							(storePassContext !== undefined ? storePassContext : passStoreContext) &&
							{storeContext}
						)}
						{...mappedStates}
						{...props}
					/>
				);
			}

			_updateStoreState() {
				const mappedStates = this._getMappedStates();

				if(!this._isEqualState(mappedStates, this._previousMappedState)) {
					this._mounted && this.setState({mappedStates});

					this._previousMappedState = mappedStates;
				}
			}

			_getMappedStates(storeContext = this.props.storeContext) {
				if(storeContext) {
					const state = storeContext.getState();

					return (this.props.mapStatesToProps || mapper)(state, this.props);
				}

				throw Error(missingProviderErrorMessage);
			}

			_isEqualState(state1, state2) {
				if(IS.fnc(this.props.storeComparator || comparator))
					return (this.props.storeComparator || comparator)(state1, state2);

				if(this.props.storeDeepCheck !== undefined ? this.props.storeDeepCheck : deepCheck) {
					return IS.equal(state1, state2);
				}

				return this._shallowEqual(state1, state2);
			}

			_shallowEqual(objA, objB) {
				const __is = (x, y) => {
					if(x === y) {
						return x !== 0 || y !== 0 || 1 / x === 1 / y;
					}
					return x !== x && y !== y;
				};

				if(__is(objA, objB)) return true;

				if(
					typeof objA !== 'object' ||
					objA === null ||
					typeof objB !== 'object' ||
					objB === null
				) {
					return false;
				}

				const keysA = Object.keys(objA);
				const keysB = Object.keys(objB);

				if(keysA.length !== keysB.length) return false;

				for(let i = 0; i < keysA.length; i++) {
					if(
						!Object.prototype.hasOwnProperty.call(objB, keysA[i]) ||
						!__is(objA[keysA[i]], objB[keysA[i]])
					) {
						return false;
					}
				}

				return true;
			}

			static get displayName() {
				return "withStoreConnection(" + (ChildComponent.displayName || ChildComponent.name) + ")"
			}

			static get wrappedComponent() {
				return ChildComponent;
			}

			static get propTypes() {
				return {
					//Optional mapper override
					mapStatesToProps: PropTypes.func,
					//Optional deep check override
					storeDeepCheck: PropTypes.bool,
					//Optional comparator override
					storeComparator: PropTypes.func,
					//Optional pass store context override
					storePassContext: PropTypes.bool,

					//withStoreContext
					storeContext: PropTypes.object.isRequired,
				}
			}
		}

		return withStoreContext(HOC, Context);
	}
};

export default withStoreConnection;
